// ProductGridCart.js
import React, { useState, useMemo, useEffect } from 'react';
import { getImageUrl } from "../../../lib/helpers";
import notFoundImage from "../../../assets/fox-not-found.webp";
import { effectiveUnitPrice } from "./pricing";

// Icono por tipo de ubicación (almacén principal / nevera / armario / sub-almacén)
const LOC_ICON = { principal: '🏠', nevera: '🧊', armario: '🗄️', sub_almacen: '📦' };
const locIcon = (kind) => LOC_ICON[kind] || '📍';
const fmtLocQty = (n) => Number(n || 0).toLocaleString('es-ES', { maximumFractionDigits: 3 });

const ProductGridCart = ({ products, loading, onAddToCart, costLine, discountRate = 0, discountLabel = null, stockByLoc = {}, bottomSpacerPx = 0 }) => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [draft, setDraft] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const categories = useMemo(() => {
    const categorySet = new Set();
    categorySet.add("All");
    let hasUncategorizedLike = false;
    // Agregar categorías válidas (filtrar null, undefined y "Uncategorized"/"uncategorized")
    (products || []).forEach(p => {
      const categoryName = p.category?.name;
      if (categoryName && !["Uncategorized", "uncategorized"].includes(categoryName)) {
        categorySet.add(categoryName);
      } else if (!categoryName || ["Uncategorized", "uncategorized"].includes(categoryName)) {
        hasUncategorizedLike = true;
      }
    });
    // Agregar "Sin categoría" si hay productos sin categoría o con nombre "Uncategorized"
    if (hasUncategorizedLike) {
      categorySet.add("Sin categoría");
    }
    return Array.from(categorySet);
  }, [products]);

  // Si la categoría activa ya no existe en el surtido actual (p. ej. dispensario → productos), volver a Todas
  useEffect(() => {
    if (selectedCategory !== 'All' && !categories.includes(selectedCategory)) {
      setSelectedCategory('All');
    }
  }, [categories, selectedCategory]);

  // Filtrar por categoría y término de búsqueda
  const filtered = useMemo(() => {
    if (!products || products.length === 0) return [];
    return products.filter(p => {
      let matchesCategory = false;
      if (selectedCategory === "All") {
        matchesCategory = true;
      } else if (selectedCategory === "Sin categoría") {
        const n = p.category?.name;
        matchesCategory = !n || ["Uncategorized", "uncategorized", "Sin categoría"].includes(n);
      } else {
        matchesCategory = p.category?.name === selectedCategory;
      }
    const matchesSearch = !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  }, [products, selectedCategory, searchTerm]);

  if (loading) {
    return (
      <div className='wa-dispensario-products p-4'>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mb-4"></div>
            <p className="text-gray-500">Cargando productos...</p>
          </div>
        </div>
      </div>
    );
  }

  const updateDraft = (id, key, value) => {
    setDraft(d => ({ ...d, [id]: { ...d[id], [key]: value } }));
  };

  return (
    <div className='wa-dispensario-products min-w-0'>
      <div className="wa-dispensario-products-filters">
        {/* Tabs de categoría (Figma) */}
        <div className="mb-3 flex flex-wrap items-center gap-1 px-1">
          {categories.map(cat => {
            const label = cat === "All" ? "Todo" : cat;
            const active = selectedCategory === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  active
                    ? "bg-[#F5F5F5] text-[#0A0A0A]"
                    : "text-[#737373] hover:text-[#0A0A0A] hover:bg-[#FAFAFA]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Buscador de productos (Figma) */}
        <div className="mb-3 px-1">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373] w-4 h-4 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar producto"
              className="w-full h-9 rounded-md border border-[#E5E5E5] bg-white pl-9 pr-8 text-sm text-[#0A0A0A] placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#0A0A0A]"
                title="Limpiar búsqueda"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mensaje cuando no hay productos */}
      {(!products || products.length === 0) && (
        <div className="text-center py-12 text-gray-500">
          <p>No hay productos disponibles</p>
        </div>
      )}

      {/* Mensaje cuando no hay productos filtrados */}
      {products && products.length > 0 && filtered.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p>
            {selectedCategory === "All"
              ? "No se encontraron productos"
              : `No se encontraron productos en la categoría "${selectedCategory}"`}
          </p>
        </div>
      )}

      {/* Products Grid - Tamaño fijo, sin estirar */}
      {filtered.length > 0 && (
      <div className="wa-dispensario-products-grid">
        {filtered.map(product => {
          const { id, name, unit_type, image_path } = product;
          // DISPONIBLE = físico − reservado en carritos abiertos (backend). El FE muestra y
          // opera sobre el disponible; cae al físico (stock_quantity) si el backend aún no
          // manda `available`. Ver docs/ARQUITECTURA_STOCK.md. Toda la lógica de abajo usa
          // esta variable, por eso se conserva el nombre `stock_quantity`.
          const stock_quantity = product.available != null ? Number(product.available) : Number(product.stock_quantity);
          // Precio según la línea de coste del socio (cae al normal si no aplica)
          const price = effectiveUnitPrice(product, costLine);
          // Peso por gramo con 3 decimales (como la báscula): step 0.001 para que
          // no se pierda precisión al teclear/guardar (3 cifras).
          const step = unit_type === "weight" ? 0.001 : 1;
          const unitLabel = unit_type === "weight" ? "g" : "pcs";
          const { qty = "", cost = "", bascula = "" } = draft[id] || {};

          // Descuento del socio aplicado al PRECIO efectivo. Por créditos (Euro): los
          // créditos son el importe FINAL que gasta el socio, así que los gramos se
          // calculan al precio CON descuento — mismo precio efectivo que la vía por peso.
          // (Bug previo: usaba el precio sin descuento → cantidad incorrecta.)
          const rate = Number(discountRate) || 0;
          const effPrice = parseFloat(price) * (1 - rate);
          const priceForCredits = effPrice > 0 ? effPrice : parseFloat(price);

          const displayedCost = unit_type === "weight"
            ? qty ? (parseFloat(qty) * parseFloat(price)).toFixed(2) : cost
            : "";
          const displayedQty = unit_type === "weight"
            ? cost ? (parseFloat(cost) / priceForCredits).toFixed(3) : qty
            : "";

          // Subtotal/total SIEMPRE desde los gramos canónicos (displayedQty), para que
          // coincida con lo que se añade al carrito tanto por peso como por créditos.
          const grams = parseFloat(displayedQty) || 0;
          const lineSubtotal = unit_type === "weight"
            ? grams * parseFloat(price)
            : (displayedCost ? parseFloat(displayedCost) : 0);
          const lineTotal = lineSubtotal * (1 - rate);
          const pct = Math.round(rate * 100);

          return (
            <div
              key={id}
              className="flex flex-col rounded-xl border border-[#E5E5E5] bg-white p-1.5 shadow-sm transition-shadow hover:shadow-md text-xs"
            >
              {/* Image con aspect ratio fijo */}
              <div className="wa-dispensario-product-image-slot relative w-full overflow-hidden rounded-lg bg-[#F8FAFC]">
                <img
                  src={(image_path ? getImageUrl(image_path) : null) || notFoundImage}
                  alt={name}
                  loading="lazy"
                  decoding="async"
                  className="absolute top-0 left-0 w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = notFoundImage;
                  }}
                />
              </div>

              {/* Chip de categoría (debajo de la imagen, Figma) */}
              <span className="mt-1 inline-flex w-fit rounded bg-[#F5F5F5] px-2 py-0.5 text-[10px] font-medium text-[#525252]">
                {(() => {
                  const n = product.category?.name;
                  if (!n) return 'Sin categoría';
                  return ["Uncategorized", "uncategorized"].includes(n) ? 'Sin categoría' : n;
                })()}
              </span>

              <h3 className="mt-0.5 text-sm font-medium text-[#0A0A0A] truncate leading-tight">{name}</h3>
              <p className="text-[11px] text-[#737373] flex items-center gap-1">
                <span>{Number(price).toFixed(2)} €/{unit_type === "weight" ? "g" : "u"}</span>
                {rate > 0 && (
                  <span className="inline-flex items-center rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" title={discountLabel ? `Descuento del socio: ${discountLabel}` : 'Descuento del socio'}>
                    −{pct}% dto
                  </span>
                )}
              </p>
              {/* Stock destacado por color (verde con stock, rojo agotado) */}
              <p className={`text-[10px] font-semibold mb-1 ${stock_quantity > 0 ? 'text-[#4D7C0F]' : 'text-[#DC2626]'}`}>
                Stock: {stock_quantity}{unit_type === "weight" ? "g" : "pcs"}
              </p>
              {(stockByLoc[String(id).toLowerCase()]?.length > 0) && (
                <div className="flex flex-wrap gap-1 mb-1">
                  {stockByLoc[String(id).toLowerCase()].map((l) => {
                    const neg = Number(l.quantity) < 0;
                    return (
                    <span key={l.location_id} className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-medium ${neg ? 'bg-red-50 text-[#DC2626] ring-1 ring-red-200' : 'bg-[#F0F0F0] text-[#737373]'}`} title={neg ? `${l.name} · en negativo, pendiente de reponer` : l.name}>
                      <span aria-hidden>{neg ? '⚠️' : locIcon(l.kind)}</span>
                      <span className="max-w-[70px] truncate">{l.name}</span>
                      <span className={`font-semibold ${neg ? 'text-[#DC2626]' : 'text-[#0A0A0A]'}`}>{fmtLocQty(l.quantity)}{unit_type === "weight" ? "g" : "u"}</span>
                    </span>
                    );
                  })}
                </div>
              )}

              {unit_type === "fixed" ? (
                <button
                  data-testid={`add-to-cart-${id}`}
                  onClick={(e) => {
                    // Validar stock antes de agregar
                    if (stock_quantity < 1) {
                      alert(`Stock insuficiente. Disponible: ${stock_quantity} ${unitLabel}`);
                      return;
                    }
                    onAddToCart({ ...product, quantity: 1 }, e);
                  }}
                  disabled={stock_quantity < 1}
                  className={`mt-auto w-full inline-flex items-center justify-center gap-1 rounded-md py-1.5 text-xs font-semibold transition-colors touch-manipulation ${
                    stock_quantity < 1
                      ? 'bg-[#F5F5F5] text-[#A3A3A3] cursor-not-allowed'
                      : 'bg-lime-500 hover:bg-lime-600 active:scale-95 text-white'
                  }`}
                  title={stock_quantity < 1 ? 'Stock insuficiente' : 'Agregar al carrito'}
                >
                  {stock_quantity < 1 ? 'Sin stock' : '+ Añadir'}
                </button>
              ) : (
                <>
                  {/* 3 filas compactas: label + box en la misma línea */}
                  <div className="space-y-0.5 mb-1">
                    <div className="flex items-center gap-2">
                      <label className="w-16 shrink-0 text-[10px] font-medium text-[#737373]">Peso</label>
                      <input
                        type="number"
                        step={step}
                        min={step}
                        max={stock_quantity}
                        value={displayedQty}
                        onChange={e => {
                          const value = e.target.value;
                          const numValue = parseFloat(value);
                          if (numValue > stock_quantity) {
                            alert(`Stock disponible: ${stock_quantity}${unitLabel}`);
                            return;
                          }
                          updateDraft(id, 'qty', value);
                          updateDraft(id, 'cost', '');
                        }}
                        className="flex-1 min-w-0 h-6 rounded-md border border-[#E5E5E5] bg-white px-2 text-xs text-[#0A0A0A] appearance-none focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                        placeholder="0.0"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="w-16 shrink-0 text-[10px] font-medium text-[#737373]">Euro</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={stock_quantity * price}
                        value={displayedCost}
                        onChange={e => {
                          const value = e.target.value;
                          const numValue = parseFloat(value);
                          const maxCost = stock_quantity * price;
                          if (numValue > maxCost) {
                            alert(`Máximo disponible: €${maxCost.toFixed(2)}`);
                            return;
                          }
                          updateDraft(id, 'cost', value);
                          updateDraft(id, 'qty', '');
                        }}
                        className="flex-1 min-w-0 h-6 rounded-md border border-[#E5E5E5] bg-white px-2 text-xs text-[#0A0A0A] appearance-none focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                        placeholder="0.0"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="w-16 shrink-0 text-[10px] font-medium text-[#737373]" title="Peso real de la báscula (merma)">Báscula</label>
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={bascula}
                        onChange={e => updateDraft(id, 'bascula', e.target.value)}
                        className="flex-1 min-w-0 h-6 rounded-md border border-[#E5E5E5] bg-white px-2 text-xs text-[#0A0A0A] appearance-none focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                        placeholder="real (opcional)"
                      />
                    </div>
                  </div>
                  {/* Total de la línea ya calculado (con descuento del socio si tiene) */}
                  {(displayedQty || displayedCost) && (
                    <div className="mb-1 flex items-center justify-between rounded-md bg-[#F7F7F3] px-2 py-1 text-[11px]">
                      <span className="text-[#737373]">{rate > 0 ? `Total (−${pct}%)` : 'Total'}</span>
                      <span className="font-semibold text-[#0A0A0A]">
                        {rate > 0 && (
                          <span className="mr-1 text-[10px] font-normal text-[#A3A3A3] line-through">€{lineSubtotal.toFixed(2)}</span>
                        )}
                        €{lineTotal.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <button
                    data-testid={`add-to-cart-${id}`}
                    onClick={(e) => {
                      const finalQty = parseFloat(displayedQty);
                      if (!finalQty || finalQty <= 0) {
                        alert('Por favor, ingresa una cantidad válida');
                        return;
                      }
                      const realW = parseFloat(bascula);
                      // Guardarraíl de merma: si se pesa en báscula, la merma (real − cobrado)
                      // no puede superar ±0,10 g (tolerancia de báscula) → bloquea y avisa.
                      const MERMA_MAX = 0.10;
                      if (!isNaN(realW) && realW > 0) {
                        const merma = realW - finalQty;
                        if (Math.abs(merma) > MERMA_MAX) {
                          alert(
                            `Merma fuera de rango: ${merma > 0 ? '+' : ''}${merma.toFixed(3)}${unitLabel}. ` +
                            `No puede superar ±0,10 g. Revisa el peso de la báscula.`
                          );
                          return;
                        }
                      }
                      // Validar stock contra lo que se descuenta (báscula si existe, si no lo cobrado)
                      const deduct = (!isNaN(realW) && realW > 0) ? realW : finalQty;
                      if (deduct > stock_quantity) {
                        alert(`Stock insuficiente. Disponible: ${stock_quantity}${unitLabel}`);
                        return;
                      }
                      onAddToCart({ ...product, quantity: finalQty, realWeight: (!isNaN(realW) && realW > 0) ? realW : undefined }, e);
                      setDraft(d => ({ ...d, [id]: { qty: "", cost: "", bascula: "" } }));
                    }}
                    disabled={stock_quantity <= 0}
                    className={`mt-auto w-full inline-flex items-center justify-center gap-1 rounded-md py-1.5 text-xs font-semibold transition-colors touch-manipulation ${
                      stock_quantity <= 0
                        ? 'bg-[#F5F5F5] text-[#A3A3A3] cursor-not-allowed'
                        : (displayedQty || displayedCost)
                          ? 'bg-lime-500 hover:bg-lime-600 active:scale-95 text-white'
                          : 'border border-[#E5E5E5] bg-white text-[#0A0A0A] hover:bg-[#F7F7F3]'
                    }`}
                    title={stock_quantity <= 0 ? 'Sin stock disponible' : 'Agregar al carrito'}
                  >
                    {stock_quantity <= 0 ? 'Sin stock' : '+ Añadir'}
                  </button>
                </>
              )}
            </div>
          );
        })}
        {bottomSpacerPx > 0 && (
          <div
            aria-hidden
            className="pointer-events-none"
            style={{ gridColumn: '1 / -1', height: bottomSpacerPx }}
          />
        )}
      </div>
      )}
    </div>
  );
};

export default ProductGridCart;
